import { authApi, useAuthStore, useLogout } from "@/features/auth";
import { userApi } from "@/features/user/user.api";
import { useUserStore } from "@/features/user/user.store";
import { defaultClient } from "@/shared/lib/axios";
import { useViewportStore } from "@/shared/stores/viewport.store";
import { HStack, Icon, VStack } from "@/shared/ui/primitives";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const NavBar = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user, accessToken, refreshToken } = useAuthStore((state) => state);
	const hydrationAttemptKeyRef = useRef<string | null>(null);
	const { pathname } = useRouterState({ select: (s) => s.location });
	const isMobile = useViewportStore((s) => s.isMobile);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const navigateWithScrollTop = (to: "/" | "/company" | "/pricing") => {
		const scrollRoot = document.getElementById("root");
		if (scrollRoot) {
			scrollRoot.scrollTo({ top: 0, left: 0, behavior: "auto" });
		} else {
			window.scrollTo({ top: 0, left: 0, behavior: "auto" });
		}
		navigate({ to });
	};

	const hasSession = !!user || !!accessToken || !!refreshToken;
	const logout = useLogout();

	useEffect(() => {
		if (user) {
			hydrationAttemptKeyRef.current = null;
			return;
		}

		const attemptKey = `${accessToken ?? ""}:${refreshToken ?? ""}`;
		if (hydrationAttemptKeyRef.current === attemptKey) {
			return;
		}
		hydrationAttemptKeyRef.current = attemptKey;

		void (async () => {
			try {
				let currentAccessToken = accessToken;
				let currentRefreshToken = refreshToken;

				if (!currentAccessToken && currentRefreshToken) {
					const refreshed = await authApi.refresh({ refreshToken: currentRefreshToken });
					currentAccessToken = refreshed.accessToken;
					useAuthStore.getState().setAccessToken(currentAccessToken);
				}

				// Store can be temporarily empty after OAuth redirect; recover from HttpOnly cookie.
				if (!currentAccessToken && !currentRefreshToken) {
					const { data } = await defaultClient.post<{ accessToken: string }>("/auth/refresh", {});
					currentAccessToken = data.accessToken;
					currentRefreshToken = useAuthStore.getState().refreshToken;
					useAuthStore.getState().setAccessToken(currentAccessToken);
				}

				if (!currentAccessToken) {
					return;
				}

				const me = await userApi.getMe();
				useAuthStore.getState().setAuth({
					accessToken: currentAccessToken,
					refreshToken: currentRefreshToken ?? useAuthStore.getState().refreshToken ?? "",
					user: {
						id: me.id,
						email: me.email,
						name: me.name,
						plan: me.plan,
						gyulBalance: me.gyulBalance,
					},
				});
				useUserStore.getState().setMe(me);
			} catch {
				// Keep existing token state; route guards will handle truly invalid sessions.
			}
		})();
	}, [accessToken, refreshToken, user]);

	const handleLogout = () => {
		logout.mutate(undefined, {
			onSuccess: () => navigate({ to: "/login", replace: true }),
		});
	};

	useEffect(() => {
		setIsMenuOpen(false);
	}, [pathname]);

	return (
		<>
			<HStack className="w-full fixed top-0 z-100 justify-center bg-bg-weak/40 backdrop-blur-lg pt-[16px]">
				<HStack className="w-full max-w-[1200px] justify-between p-[10px] pl-[16px]">
					<button onClick={() => navigateWithScrollTop("/")} className="text-pm-strong">
						<Icon name="Logo" width={isMobile ? 100 : 135} height={isMobile ? 27 : 36} viewBox="0 0 180 48" />
					</button>
					<HStack className="hidden md:flex gap-[10px] text-md font-medium">
						<HStack>
							<button
								onClick={() => navigateWithScrollTop("/company")}
								className={`px-[20px] py-[10px] rounded-full ${pathname === "/company" ? "text-ct-strong" : "text-ct-weak"}`}
							>
								{t("sitemap.company")}
							</button>
							<button
								onClick={() => navigateWithScrollTop("/pricing")}
								className={`px-[20px] py-[10px] rounded-full ${pathname === "/pricing" ? "text-ct-strong" : "text-ct-weak"}`}
							>
								{t("sitemap.pricing")}
							</button>
							{hasSession && (
								<button
									onClick={handleLogout}
									className="px-[20px] py-[10px] rounded-full text-ct-weak hover:text-ct-strong"
								>
									<HStack className="gap-[6px] items-center">
										<Icon name="Logout" width={18} height={18} />
										{t("sitemap.logout")}
									</HStack>
								</button>
							)}
						</HStack>
						<button
							onClick={() => navigate({ to: hasSession ? "/workflow" : "/login" })}
							className="px-[20px] py-[10px] bg-pm-strong rounded-full text-ct-elevated"
						>
							{t(hasSession ? "sitemap.workflow" : "sitemap.login")}
						</button>
					</HStack>
					<HStack className="flex md:hidden gap-[8px] items-center">
						<button
							onClick={() => navigate({ to: hasSession ? "/workflow" : "/login" })}
							className="px-[14px] py-[8px] bg-pm-strong rounded-full text-ct-elevated text-sm font-medium"
						>
							{t(hasSession ? "sitemap.workflow" : "sitemap.login")}
						</button>
						<button
							className="p-[8px] rounded-[10px] text-ct-strong"
							onClick={() => setIsMenuOpen((v) => !v)}
							aria-label={t("navbar.menu")}
						>
							<Icon name={isMenuOpen ? "Close" : "Menu"} width={22} height={22} />
						</button>
					</HStack>
				</HStack>
			</HStack>
			<AnimatePresence>
				{isMenuOpen && isMobile && (
					<motion.div
						className="fixed inset-0 z-90 bg-bg-weak/95 backdrop-blur-md pt-[80px]"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
					>
						<VStack className="w-full gap-[4px] p-[20px] items-start">
							<button
								onClick={() => { setIsMenuOpen(false); navigateWithScrollTop("/company"); }}
								className={`w-full text-left px-[16px] py-[14px] rounded-[12px] text-md font-medium ${pathname === "/company" ? "text-ct-strong bg-bg-regular/60" : "text-ct-regular"}`}
							>
								{t("sitemap.company")}
							</button>
							<button
								onClick={() => { setIsMenuOpen(false); navigateWithScrollTop("/pricing"); }}
								className={`w-full text-left px-[16px] py-[14px] rounded-[12px] text-md font-medium ${pathname === "/pricing" ? "text-ct-strong bg-bg-regular/60" : "text-ct-regular"}`}
							>
								{t("sitemap.pricing")}
							</button>
							{hasSession && (
								<button
									onClick={() => { setIsMenuOpen(false); handleLogout(); }}
									className="w-full text-left px-[16px] py-[14px] rounded-[12px] text-md font-medium text-ct-regular"
								>
									<HStack className="gap-[8px] items-center">
										<Icon name="Logout" width={18} height={18} />
										{t("sitemap.logout")}
									</HStack>
								</button>
							)}
						</VStack>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};
